import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Box, Typography, Button, ButtonGroup, Card, CardContent, Grid } from "@mui/material";
import { Chart, registerables } from "chart.js";
import { getKpisHistory } from "services/api";
import PropTypes from "prop-types";
import LoadingScreen from "components/Loading";
import "chartjs-adapter-date-fns";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import { getPerformanceHistory } from "services/api";

Chart.register(...registerables);

const COLORS = ["#007bff", "#28a745", "#dc3545", "#ffc107", "#6f42c1", "#17a2b8"];

const transformKpiHistory = (data) => {
  return data.map(([kpiName, averageResult, month, year]) => ({
    kpiName,
    year,
    month,
    averageResult: averageResult !== null ? averageResult : null,
  }));
};

const transformKpiHistoryPerformance = (data) => {
  return data.map(([averageResult, month, year]) => ({
    year,
    month,
    averageResult: averageResult !== null ? averageResult : null, // Ensure null stays null
  }));
};

const KPIHistoryChart = ({ employee, isMe }) => {
  const { t } = useTranslation();
  const [historyData, setHistoryData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [performanceChartData, setPerformanceChartData] = useState(null);
  const [performanceHistoryData, setPerformanceHistoryData] = useState([]);
  const [selectedView, setSelectedView] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getKpisHistory(employee.id);
      if (data !== "error") {
        setHistoryData(transformKpiHistory(data));
      }
      const data2 = await getPerformanceHistory(employee.id);
      if (data2 !== "error") {
        setPerformanceHistoryData(transformKpiHistoryPerformance(data2));
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (historyData.length > 0) {
      setChartData(formatChartData(historyData, selectedView));
    }

    if (performanceHistoryData) {
      const filteredData = filterPerformanceData(selectedView);
      setPerformanceChartData(formatPerformanceChartData(filteredData));
    }
  }, [historyData, selectedView, performanceHistoryData]);

  const filterPerformanceData = (range) => {
    const currentYear = new Date().getFullYear();

    let pastYearLimit;
    switch (range) {
      case "thisYear":
        pastYearLimit = currentYear;
        break;
      case "lastYear":
        pastYearLimit = currentYear - 1;
        break;
      case "last2Years":
        pastYearLimit = currentYear - 2;
        break;
      case "all":
        pastYearLimit = 0; // Include all years
        break;
      default:
        pastYearLimit = currentYear;
    }

    return performanceHistoryData
      .filter((data) => data.year >= pastYearLimit)
      .sort((a, b) => new Date(a.year, a.month - 1) - new Date(b.year, b.month - 1));
  };
  const formatPerformanceChartData = (data) => {
    const labels = data.map((item) => `${item.month}/${item.year}`);
    const values = data.map((item) => item.averageResult);

    return {
      labels,
      datasets: [
        {
          label: t("user.dashboards.performance"),
          data: values,
          borderColor: "blue",
          backgroundColor: "rgba(0, 123, 255, 0.5)",
          tension: 0.4,
        },
      ],
    };
  };
  const formatChartData = (data, view) => {
    const now = new Date();
    const currentYear = now.getFullYear();

    let filteredData;
    switch (view) {
      case "thisYear":
        filteredData = data.filter((item) => item.year === currentYear);
        break;
      case "lastYear":
        filteredData = data.filter((item) => item.year >= currentYear - 1);
        break;
      case "last2Years":
        filteredData = data.filter((item) => item.year >= currentYear - 2);
        break;
      default:
        filteredData = data;
        break;
    }
    const kpiGroups = {};
    filteredData.forEach((item) => {
      if (!kpiGroups[item.kpiName]) {
        kpiGroups[item.kpiName] = [];
      }
      kpiGroups[item.kpiName].push({
        x: new Date(item.year, item.month - 1),
        y: item.averageResult,
      });
    });
    if (!kpiGroups) {
      return;
    }
    return {
      datasets: Object.keys(kpiGroups).map((kpiName, index) => ({
        label: kpiName,
        data: kpiGroups[kpiName],
        borderColor: COLORS[index % COLORS.length],
        backgroundColor: COLORS[index % COLORS.length] + "55",
        fill: false,
        tension: 0.4,
      })),
    };
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const chartOptions = {
    responsive: true,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "month",
          displayFormats: {
            month: "MMM yyyy",
          },
        },
        title: {
          display: true,
          text: t("user.myScorecard.xAxisTitle"),
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("user.myScorecard.yAxisTitle"),
        },
      },
    },
  };

  const chartOptions2 = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          stepSize: 10,
        },
      },
    },
  };

  const getBestMonth = () => {
    if (!performanceHistoryData || performanceHistoryData.length === 0) return null;

    const currentYear = new Date().getFullYear();

    const thisYearData = performanceHistoryData.filter((item) => item.year === currentYear);

    if (thisYearData.length === 0) return null;

    const best = thisYearData.reduce((a, b) => (b.averageResult > a.averageResult ? b : a));

    return {
      month: best.month,
      year: best.year,
      value: best.averageResult?.toFixed(2) ?? "0.00",
    };
  };

  const getBestAndWorstKPIThisYear = () => {
    const currentYear = new Date().getFullYear();

    // Filter data for the current year and ensure valid average results
    const currentYearData = historyData.filter(
      (item) => item.year === currentYear && item.averageResult !== null
    );

    if (currentYearData.length === 0) return { best: null, worst: null };

    // Group results by KPI name
    const grouped = currentYearData.reduce((acc, item) => {
      if (!acc[item.kpiName]) acc[item.kpiName] = [];
      acc[item.kpiName].push(item.averageResult);
      return acc;
    }, {});

    // Calculate average for each KPI
    const averages = Object.entries(grouped).map(([kpiName, values]) => ({
      kpiName,
      avg: values.reduce((a, b) => a + b, 0) / values.length,
    }));

    // Determine best and worst KPI
    const best = averages.reduce((a, b) => (b.avg > a.avg ? b : a));
    const worst = averages.reduce((a, b) => (b.avg < a.avg ? b : a));

    return {
      best: { name: best.kpiName, value: best.avg.toFixed(2) },
      worst: { name: worst.kpiName, value: worst.avg.toFixed(2) },
    };
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">
        {" "}
        {isMe
          ? t("user.myScorecard.title.my")
          : t("user.myScorecard.title.other", { name: employee.name })}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4, mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">{t("user.myScorecard.bestMonth")}</Typography>
              {getBestMonth() ? (
                <>
                  <Typography>
                    {getBestMonth().month}/{getBestMonth().year}
                  </Typography>
                  <Typography color="primary" fontWeight="bold">
                    {getBestMonth().value}%
                  </Typography>
                </>
              ) : (
                <Typography>{t("user.myScorecard.noData")}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">{t("user.myScorecard.bestKPIThisYear")}</Typography>
              {getBestAndWorstKPIThisYear().best ? (
                <>
                  <Typography>{getBestAndWorstKPIThisYear().best.name}</Typography>
                  <Typography color="success.main" fontWeight="bold">
                    {getBestAndWorstKPIThisYear().best.value}%
                  </Typography>
                </>
              ) : (
                <Typography>{t("user.myScorecard.noData")}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">{t("user.myScorecard.worstKPIThisYear")}</Typography>
              {getBestAndWorstKPIThisYear().worst ? (
                <>
                  <Typography>{getBestAndWorstKPIThisYear().worst.name}</Typography>
                  <Typography color="error.main" fontWeight="bold">
                    {getBestAndWorstKPIThisYear().worst.value}%
                  </Typography>
                </>
              ) : (
                <Typography>{t("user.myScorecard.noData")}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ width: "100%", maxWidth: 900, margin: "auto", textAlign: "center" }}>
        {/* Time Range Buttons */}
        <ButtonGroup variant="contained" sx={{ mb: 3 }}>
          <MDButton
            onClick={() => setSelectedView("thisYear")}
            color="primary"
            variant={selectedView === "thisYear" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.thisYear")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("lastYear")}
            color="primary"
            variant={selectedView === "lastYear" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.lastYear")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("last2Years")}
            color="primary"
            variant={selectedView === "last2Years" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.last2Years")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("all")}
            color="primary"
            variant={selectedView === "all" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.allTime")}
          </MDButton>
        </ButtonGroup>

        {/* Line Chart */}
        <Box sx={{ width: "100%", maxWidth: 1200, margin: "auto", textAlign: "center" }}>
          {/* Time Range Buttons */}
          <ButtonGroup variant="contained" sx={{ mb: 3 }}>
            {/* ...existing MDButtons */}
          </ButtonGroup>

          {/* Charts */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {chartData ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <Typography>{t("user.myScorecard.noDataFound")}</Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {performanceChartData ? (
                <Line data={performanceChartData} options={chartOptions2} />
              ) : (
                <Typography>{t("user.myScorecard.noDataFound")}</Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

KPIHistoryChart.propTypes = {
  employee: PropTypes.object.isRequired,
  isMe: PropTypes.bool.isRequired,
};

export default KPIHistoryChart;
